/**
 * Assessment Service
 * Handles assessment-related API calls including CRUD, items, and reports
 */
import BaseApiService from '../base/BaseApiService';
import config from '@config';
import authService from '../authServiceFactory';
import type {
  Assessment,
  AssessmentFilters,
  AssessmentsResponse,
  CreateAssessmentRequest,
  UpdateAssessmentRequest,
  AssessmentItemsResponse,
  AssessmentItem,
  UpdateAssessmentItemRequest,
  UpdateAssessmentItemResponse,
  CreateAssessmentResponse,
  AssessmentReport
} from '@app-types';
import { toApiError } from '@utils/apiError';

class AssessmentService extends BaseApiService {
  /**
   * Get assessments list with optional filters
   */
  async getAssessments(filters: AssessmentFilters = {}): Promise<AssessmentsResponse> {
    const params = this.buildQueryParams(filters);
    return this.get<AssessmentsResponse>(`/assessments?${params}`);
  }

  /**
   * Get all assessments matching the given filters, ignoring pagination — used for
   * full-dataset export.
   */
  async getAssessmentsForExport(
    filters: Omit<AssessmentFilters, 'limit' | 'offset'> = {}
  ): Promise<{ total: number; data: Assessment[] }> {
    const params = this.buildQueryParams(filters);
    return this.get<{ total: number; data: Assessment[] }>(`/assessments/export?${params}`);
  }

  /**
   * Get a single assessment by ID
   */
  async getAssessment(id: number): Promise<Assessment> {
    return this.get<Assessment>(`/assessments/${id}`);
  }

  /**
   * Create a new assessment
   */
  async createAssessment(data: CreateAssessmentRequest): Promise<Assessment> {
    return this.post<Assessment>('/assessments', data);
  }

  /**
   * Update an existing assessment
   */
  async updateAssessment(id: number, data: UpdateAssessmentRequest): Promise<Assessment> {
    return this.put<Assessment>(`/assessments/${id}`, data);
  }

  /**
   * Complete an assessment
   */
  async completeAssessment(id: number): Promise<{ message: string; assessment: Assessment }> {
    return this.post<{ message: string; assessment: Assessment }>(`/assessments/${id}/complete`);
  }

  /**
   * Generate and download PDF report for an assessment
   * Special case: Uses fetch API directly for file download
   */
  async generateAssessmentReport(assessmentId: number): Promise<void> {
    const service = await authService;
    const token = await service.getAccessToken();

    const response = await fetch(`${config.apiBaseUrl}/assessments/${assessmentId}/generate-report`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to generate report' }));
      throw new Error(errorData.error || 'Failed to generate report');
    }

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `assessment-${assessmentId}-report.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Download the PDF
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Cancel an assessment. A reason is required by the backend once the
   * assessment is published; before publication it is optional.
   */
  async cancelAssessment(id: number, reason?: string): Promise<Assessment> {
    return this.post<Assessment>(`/assessments/${id}/cancel`, reason ? { reason } : {});
  }

  /**
   * Reassign assessment to a different assessor
   * Specific method for single assessor rule
   */
  async reassignAssessment(assessmentId: number, newAssessorId: number): Promise<Assessment> {
    return this.updateAssessment(assessmentId, { assessor_id: newAssessorId });
  }

  /**
   * Get assessment items for an assessment
   */
  async getAssessmentItems(assessmentId: number): Promise<AssessmentItemsResponse> {
    return this.get<AssessmentItemsResponse>(`/assessments/${assessmentId}/items`);
  }

  /**
   * Generate assessment items (framework) for an assessment
   */
  async generateAssessmentItems(
    assessmentId: number
  ): Promise<{ message: string; assessment_id: number; items_generated: number }> {
    return this.post<{ message: string; assessment_id: number; items_generated: number }>(
      `/assessments/${assessmentId}/generate-items`
    );
  }

  /**
   * Get a single assessment item by ID
   */
  async getAssessmentItem(itemId: number): Promise<AssessmentItem> {
    return this.get<AssessmentItem>(`/assessment-items/${itemId}`);
  }

  /**
   * Update an assessment item
   */
  async updateAssessmentItem(itemId: number, data: UpdateAssessmentItemRequest): Promise<UpdateAssessmentItemResponse> {
    return this.put<UpdateAssessmentItemResponse>(`/assessment-items/${itemId}`, data);
  }

  /**
   * Create assessment for a single contract meeting
   */
  async createAssessmentForMeeting(
    meetingId: number,
    contractId: number
  ): Promise<{ assessment_id: number; assessment_name: string }> {
    // Use scheduler API to create assessment for a single contract
    const response = await this.post<CreateAssessmentResponse>('/assessments', {
      itemId: contractId,
      type: 'contract'
    });

    // Return first assessment (should only be one for single contract)
    const assessment = response.created_assessments[0];
    if (!assessment) {
      throw toApiError(response.errors?.[0]?.error || 'Failed to create assessment');
    }

    return {
      assessment_id: assessment.assessment_id,
      assessment_name: assessment.assessment_name
    };
  }

  /**
   * Create assessments for all contracts in a grouped meeting
   */
  async createAssessmentsForGroupMeeting(
    meetingId: number
  ): Promise<{ assessment_ids: number[]; count: number; errors?: CreateAssessmentResponse['errors'] }> {
    // Use scheduler API to create assessments for all contracts in a group
    const response = await this.post<CreateAssessmentResponse>('/assessments', {
      itemId: meetingId,
      type: 'group'
    });

    if (!response.created_assessments || response.created_assessments.length === 0) {
      throw toApiError(response.errors?.[0]?.error || 'Failed to create assessments for group');
    }

    return {
      assessment_ids: response.created_assessments.map((a) => a.assessment_id),
      count: response.created_assessments.length,
      errors: response.errors
    };
  }

  /**
   * Get list of generated reports for an assessment
   */
  async getAssessmentReports(assessmentId: number): Promise<AssessmentReport[]> {
    return this.get<AssessmentReport[]>(`/assessments/${assessmentId}/reports`);
  }

  /**
   * Get a pre-signed download URL for a specific report
   */
  async getReportDownloadUrl(assessmentId: number, reportId: number): Promise<string> {
    const response = await this.get<{ downloadUrl: string }>(
      `/assessments/${assessmentId}/reports/${reportId}/download`,
    );
    return response.downloadUrl;
  }
}

export const assessmentService = new AssessmentService();
export default assessmentService;
